/**
 * ONE FETCH WITH A CONSCIENCE, for every client core hands out.
 *
 * Content is read through here — the manifest, then the versioned files under it (`ContentClient`
 * in `./content.ts`). Every such call needs the same three things a bare `fetch` does not do: run
 * in Node and React Native as well as a browser (so the response is a structural type, not the
 * DOM's), report "no answer at all" differently from "answered 404", and GIVE UP — because a
 * request that never settles is indistinguishable from one that never arrives, and a reader has to
 * be told something either way.
 *
 * That shared half lives here so it is written, and tested, once.
 */

/**
 * As much of a response as a client reads, and no more — written out rather than taken from the
 * DOM's `Response`, because core compiles without the DOM library and must run where there is no
 * browser. A structural type is also the only kind a test can satisfy without inventing a `fetch`.
 */
export interface ApiResponse {
  ok: boolean;
  status: number;
  headers: { get(name: string): string | null };
  json(): Promise<unknown>;
}

/**
 * WHAT A REQUEST CARRIES — reads and WRITES, and it used to be only reads.
 *
 * This was `headers` and `signal`, which is everything the content plane asks for: it does nothing
 * but GET static artifacts. The social client sends the same shape with `method`, `body` and
 * `credentials` on it, and on 2026-08-18 the web app began handing it this bridge instead of the
 * global `fetch`.
 *
 * The bridges build a platform `RequestInit` **by hand** from the fields they know about, so the
 * three they did not know about were dropped on the floor: every write the social plane made left
 * the browser as a **GET with no body**. Opening a room, posting, muting, banning, renaming — all
 * of them, on both clients. Nothing caught it, and nothing could: passing a wider object where a
 * narrower type is expected is legal, so the compiler was happy, and every browser gate answers
 * `page.route('**\/rooms/join')` whatever the method, so the stub replied to the GET as cheerfully
 * as it would have to the POST. It took a live server to say "Cannot GET".
 *
 * All four fields are plain data, so core stays DOM-free and one bridge serves both planes.
 */
export interface ApiRequestInit {
  headers?: Record<string, string>;
  signal?: unknown;
  method?: string;
  body?: string;
  /** The web's session cookie rides on this; the phone sends a bearer header instead. */
  credentials?: 'include' | 'same-origin';
}

export type ApiFetch = (url: string, init?: ApiRequestInit) => Promise<ApiResponse>;

/** What cancels a request that is taking too long. `AbortController`, where there is one. */
export interface Canceller {
  signal: unknown;
  abort(): void;
}

/** A request that did not succeed, with enough to say what a reader should be told. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Whether asking again could plausibly work. A 404 will not fix itself. */
  get worthRetrying(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

/** Only `AbortController` if the platform has one — Node, browsers and React Native all do. */
export function defaultCanceller(): Canceller | undefined {
  const constructor = (globalThis as { AbortController?: new () => Canceller }).AbortController;
  return constructor ? new constructor() : undefined;
}

/**
 * How long to wait, and what to wait WITH — the parts a test replaces to avoid a real clock.
 *
 * Every field is optional at the edge (a caller passes what it wants to override) and resolved to
 * a working default by {@link resolveGiveUp}, so a production caller writes `{}` and a test writes
 * the three fakes it needs.
 */
export interface GiveUpOptions {
  /** Milliseconds before one request is abandoned. Matches `ChunkLoader`'s own timeout. */
  timeoutMs?: number;
  newCanceller?: () => Canceller | undefined;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/** The same options with every default filled in — what {@link requestJson} actually reads. */
export interface ResolvedGiveUp {
  timeoutMs: number;
  newCanceller: () => Canceller | undefined;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

export function resolveGiveUp(options: GiveUpOptions = {}): ResolvedGiveUp {
  return {
    timeoutMs: options.timeoutMs ?? 12000,
    newCanceller: options.newCanceller ?? defaultCanceller,
    setTimer: options.setTimer ?? ((fn, ms) => setTimeout(fn, ms)),
    clearTimer: options.clearTimer ?? (handle => clearTimeout(handle as ReturnType<typeof setTimeout>)),
  };
}

/**
 * Fetch one URL and parse it as JSON, or throw an {@link ApiError} that names what went wrong.
 *
 * A thrown error is status 0 for "no answer at all" — offline, refused, timed out — which a reader
 * is told about differently from a 404, so the two are never folded into one. A caller that needs
 * a response header reads it off the {@link ApiFetch} directly; this returns only the body it came
 * for, which is all any content read wants.
 */
export async function requestJson<T>(
  doFetch: ApiFetch,
  url: string,
  give: ResolvedGiveUp,
): Promise<T> {
  const canceller = give.newCanceller();
  let timedOut = false;
  const timer = give.setTimer(() => {
    timedOut = true;
    canceller?.abort();
  }, give.timeoutMs);

  let response: ApiResponse;
  try {
    response = await doFetch(url, canceller ? { signal: canceller.signal } : undefined);
  } catch (cause) {
    throw new ApiError(0, url, timedOut
      ? `gave up waiting for ${url} after ${give.timeoutMs}ms`
      : `could not reach ${url}: ${String(cause)}`);
  } finally {
    give.clearTimer(timer);
  }

  if (!response.ok) throw new ApiError(response.status, url, `${url} answered ${response.status}`);
  return await response.json() as T;
}
